/**
 * Componente de Fallback para Error Boundary
 * Exibido quando um erro é capturado
 */

import React, { useState } from 'react';

function ErrorFallback({ error, errorInfo, onRetry, onGoHome }) {
  const [showDetails, setShowDetails] = useState(false);
  const isDev = import.meta.env.DEV;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Ícone de erro */}
        <div style={styles.iconContainer}>
          <svg
            style={styles.icon}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Título */}
        <h1 style={styles.title}>Ops! Algo deu errado</h1>

        {/* Mensagem */}
        <p style={styles.message}>
          Ocorreu um erro inesperado. Nossa equipe foi notificada e está
          trabalhando para resolver o problema.
        </p>

        {/* Mensagem de erro (se em dev ou clicou em detalhes) */}
        {error?.message && (showDetails || isDev) && (
          <div style={styles.errorBox}>
            <strong>Erro:</strong> {error.message}
          </div>
        )}

        {/* Botões de ação */}
        <div style={styles.buttonContainer}>
          <button
            onClick={onRetry}
            style={{ ...styles.button, ...styles.primaryButton }}
          >
            Tentar Novamente
          </button>

          <button
            onClick={onGoHome}
            style={{ ...styles.button, ...styles.secondaryButton }}
          >
            Voltar ao Início
          </button>
        </div>

        {/* Link para detalhes (em produção) */}
        {!isDev && error?.message && (
          <button
            onClick={() => setShowDetails(!showDetails)}
            style={styles.detailsLink}
          >
            {showDetails ? 'Ocultar detalhes' : 'Ver detalhes do erro'}
          </button>
        )}

        {/* Stack trace (apenas em dev) */}
        {isDev && errorInfo?.componentStack && (
          <details style={styles.details}>
            <summary style={styles.summary}>Stack do Componente</summary>
            <pre style={styles.stack}>{errorInfo.componentStack}</pre>
          </details>
        )}

        {isDev && error?.stack && (
          <details style={styles.details}>
            <summary style={styles.summary}>Stack do Erro</summary>
            <pre style={styles.stack}>{error.stack}</pre>
          </details>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: 'var(--color-background, #f5f5f5)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  card: {
    maxWidth: '500px',
    width: '100%',
    padding: '40px',
    backgroundColor: 'var(--color-card, #ffffff)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
  },
  iconContainer: {
    marginBottom: '20px',
  },
  icon: {
    width: '64px',
    height: '64px',
    color: 'var(--color-error, #e53935)',
    margin: '0 auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: 'var(--color-text, #333)',
    marginBottom: '12px',
  },
  message: {
    fontSize: '16px',
    color: 'var(--color-text-secondary, #666)',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  errorBox: {
    backgroundColor: 'var(--color-error-light, #ffebee)',
    color: 'var(--color-error, #c62828)',
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    marginBottom: '24px',
    fontSize: '14px',
    textAlign: 'left',
    wordBreak: 'break-word',
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  button: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
  },
  primaryButton: {
    backgroundColor: 'var(--color-primary, #1976d2)',
    color: '#ffffff',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    color: 'var(--color-primary, #1976d2)',
    border: '2px solid var(--color-primary, #1976d2)',
  },
  detailsLink: {
    marginTop: '20px',
    background: 'none',
    border: 'none',
    color: 'var(--color-text-secondary, #666)',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: '14px',
  },
  details: {
    marginTop: '20px',
    textAlign: 'left',
  },
  summary: {
    cursor: 'pointer',
    fontWeight: '500',
    marginBottom: '8px',
    color: 'var(--color-text-secondary, #666)',
  },
  stack: {
    backgroundColor: 'var(--color-background-alt, #f0f0f0)',
    padding: '12px',
    borderRadius: 'var(--radius-md)',
    overflow: 'auto',
    fontSize: '12px',
    maxHeight: '200px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
};

export default ErrorFallback;
